import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Pressable,
  ScrollView,
  StyleProp,
  TextInput,
  View,
  ViewStyle,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Icon } from '@/components/Icon';
import { Text } from '@/components/Text';
import colors from '@/config/colors';
import { useRotateAnimation } from '@/hooks/useRotateAnimation';
import { IS_WEB } from '@/utils/platform';
import { formatTagLabel } from '@/utils/stringHelpers';
import { styles } from './styles';

export const tagListMaxHeight = 260;

type TagOption = {
  label: string;
  value: string;
};

type TagSelectProps = {
  options: TagOption[];
  value: string[];
  onChange: (values: string[]) => void;
  onAddTag: (label: string) => void;
  maxSelected?: number;
  onMaxSelectedAttempt?: () => void;
  placeholder?: string;
  testID?: string;
  style?: StyleProp<ViewStyle>;
};

export const TagSelect = ({
  options,
  value,
  onChange,
  onAddTag,
  maxSelected,
  onMaxSelectedAttempt,
  placeholder = 'Tag hinzufügen',
  testID,
  style,
}: TagSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [reservedHeight, setReservedHeight] = useState<number | null>(null);
  const rootRef = useRef<View>(null);
  const rotate = useRotateAnimation(isOpen, 200);

  const trimmedSearch = search.trim();
  const isMaxSelected =
    maxSelected !== undefined && value.length >= maxSelected;

  const filteredOptions = useMemo(() => {
    const needle = trimmedSearch.toLowerCase();

    return needle
      ? options.filter((option) => option.label.toLowerCase().includes(needle))
      : options;
  }, [options, trimmedSearch]);

  const searchMatchesAnOption = useMemo(
    () =>
      options.some(
        (option) =>
          option.label.trim().toLowerCase() === trimmedSearch.toLowerCase(),
      ),
    [options, trimmedSearch],
  );

  const close = () => {
    setIsOpen(false);
    setSearch('');
    setReservedHeight(null);
  };

  useEffect(() => {
    if (!IS_WEB || !isOpen) {
      return;
    }

    // On web an RN View's ref is the DOM node it renders.
    const root = rootRef.current as unknown as HTMLElement | null;

    const closeOnOutsidePress = ({ target }: Event) => {
      if (!root?.contains(target as Node)) {
        close();
      }
    };

    document.addEventListener('pointerdown', closeOnOutsidePress);

    return () =>
      document.removeEventListener('pointerdown', closeOnOutsidePress);
  }, [isOpen]);

  const rejectAsMaxSelected = () => {
    close();
    onMaxSelectedAttempt?.();
  };

  const deselect = (tagValue: string) =>
    onChange(value.filter((selected) => selected !== tagValue));

  const toggle = (tagValue: string) => {
    if (value.includes(tagValue)) {
      deselect(tagValue);
      return;
    }

    if (isMaxSelected) {
      rejectAsMaxSelected();
      return;
    }

    onChange([...value, tagValue]);
  };

  const addTag = () => {
    if (!trimmedSearch || searchMatchesAnOption) {
      return;
    }

    if (isMaxSelected) {
      rejectAsMaxSelected();
      return;
    }

    onAddTag(trimmedSearch);
    setSearch('');
    close();
  };

  const renderSelectedTags = () =>
    value.map((tagValue, index) => {
      const option = options.find((item) => item.value === tagValue);

      if (!option) {
        return null;
      }

      return (
        <View key={tagValue + index} style={styles.selectedTag}>
          <Text style={styles.selectedTagLabel}>
            {formatTagLabel(option.label)}
          </Text>
          <Pressable
            onPress={() => deselect(tagValue)}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={`${option.label} entfernen`}
          >
            <Icon
              name="close"
              size={16}
              color={colors.newUI.therapist.primaryBlue80}
            />
          </Pressable>
        </View>
      );
    });

  return (
    <View ref={rootRef} style={style}>
      <View style={[styles.fieldOuter, isOpen && styles.fieldOuterOpen]}>
        <Pressable
          style={styles.field}
          onPress={isOpen ? undefined : () => setIsOpen(true)}
          accessible={false}
        >
          <View style={styles.fieldContent}>
            {renderSelectedTags()}

            {isOpen ? (
              <TextInput
                testID={testID ? `${testID}-search` : undefined}
                value={search}
                onChangeText={setSearch}
                onSubmitEditing={addTag}
                placeholder={
                  options.length > 0 ? 'Suchen...' : 'Filterbegriff hinzufügen'
                }
                placeholderTextColor={colors.newUI.therapist.grey70}
                style={styles.searchInput}
                allowFontScaling={false}
              />
            ) : value.length === 0 ? (
              <Text style={styles.placeholder}>{placeholder}</Text>
            ) : null}
          </View>

          <Pressable
            testID={testID}
            onPress={isOpen ? close : () => setIsOpen(true)}
            style={styles.chevron}
            accessibilityRole="button"
            accessibilityState={{ expanded: isOpen }}
            accessibilityLabel={placeholder}
          >
            <Animated.View style={{ transform: [{ rotate }] }}>
              <Ionicons
                name="chevron-down-outline"
                size={18}
                color={colors.newUI.therapist.primaryBlue80}
              />
            </Animated.View>
          </Pressable>
        </Pressable>
      </View>

      {isOpen ? (
        <View style={reservedHeight !== null && { height: reservedHeight }}>
          <View
            style={[
              styles.list,
              { maxHeight: reservedHeight ?? tagListMaxHeight },
            ]}
          >
            <ScrollView
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
              onContentSizeChange={(_width, height) =>
                setReservedHeight((current) =>
                  current === null
                    ? Math.min(height, tagListMaxHeight)
                    : current,
                )
              }
            >
              {filteredOptions.map((option) => {
                const isSelected = value.includes(option.value);

                return (
                  <Pressable
                    key={option.value}
                    testID={
                      testID ? `${testID}-option-${option.value}` : undefined
                    }
                    onPress={() => toggle(option.value)}
                    style={styles.optionRow}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: isSelected }}
                  >
                    <Text
                      style={[
                        styles.optionLabel,
                        isSelected && styles.optionLabelSelected,
                      ]}
                    >
                      {formatTagLabel(option.label)}
                    </Text>

                    {isSelected ? (
                      <Ionicons
                        name="checkmark"
                        size={30}
                        color={colors.newUI.therapist.green70}
                      />
                    ) : null}
                  </Pressable>
                );
              })}

              {trimmedSearch && !searchMatchesAnOption ? (
                <Pressable
                  testID={testID ? `${testID}-add` : undefined}
                  onPress={addTag}
                  style={styles.addRow}
                >
                  <Icon
                    name="plusCircle"
                    size={20}
                    color={colors.newUI.therapist.primaryBlue80}
                  />
                  <Text style={styles.addLabel}>
                    {`Hinzufügen von „${formatTagLabel(trimmedSearch)}“`}
                  </Text>
                </Pressable>
              ) : null}

              {filteredOptions.length === 0 && !trimmedSearch ? (
                <Text style={styles.emptyLabel}>
                  Noch keine Filterbegriffe vorhanden
                </Text>
              ) : null}
            </ScrollView>
          </View>
        </View>
      ) : null}
    </View>
  );
};
